import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import {
  Item,
  ItemNoAndType,
} from "@coworker/apprestructured/src/shared/types/item";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { LoadingArticle } from "../AttachArticles/LoadingArticle/LoadingArticle";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { ItemCardDefault } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardDefault/ItemCardDefault";
import DefaultScannerComponent from "@coworker/apprestructured/src/scan-and-search/DefaultScannerComponent";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { useGetItemOrLocalCombination } from "@coworker/apprestructured/src/shared/hooks/item/useGetItemOrLocalCombination";

type AttachArticleProps = {
  item: Item | null;
  setItemNoAndType: (newItemNoAndType: ItemNoAndType) => void;
  disabled: boolean;
  isError?: boolean;
};

export const AttachArticle = ({
  item,
  setItemNoAndType,
  disabled,
  isError = false,
}: AttachArticleProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleListViewItemClick = useCallback(() => {
    if (disabled) return;
    setShowModal(true);
  }, [disabled]);

  const handleCloseClick = useCallback(() => {
    setShowModal(false);
    setShowToast(false);
    setToastMessage("");
  }, []);

  const { t } = useTranslation();

  const getItemOrLocalCombination = useGetItemOrLocalCombination();

  const handleScannedArticle = useCallback(
    async (itemNo?: string) => {
      setIsCameraOpen(false);
      setShowToast(false);

      if (!itemNo || isLoading) return;

      setIsLoading(true);
      try {
        const item = await getItemOrLocalCombination(itemNo);

        if (item) {
          setItemNoAndType({ no: item.no, type: item.type });
          setShowModal(false);
        } else {
          setToastMessage(t("couldNotAddArticleString"));
          setShowToast(true);
        }
      } catch {
        setToastMessage(t("couldNotAddArticleString"));
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, getItemOrLocalCombination, setItemNoAndType, t]
  );

  const content = useMemo(() => {
    if (isCameraOpen) {
      return (
        <DefaultScannerComponent
          onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
          isCameraOpen={isCameraOpen}
          onCameraOpenChange={setIsCameraOpen}
        />
      );
    } else {
      return (
        <FixaModalBody>
          <ModalColumn gap="medium">
            <DefaultScannerComponent
              onResult={({ articleNumber }) =>
                handleScannedArticle(articleNumber)
              }
              isCameraOpen={isCameraOpen}
              onCameraOpenChange={setIsCameraOpen}
            />
            {isLoading && <LoadingArticle />}
          </ModalColumn>
        </FixaModalBody>
      );
    }
  }, [isCameraOpen, handleScannedArticle, isLoading]);

  return (
    <>
      <FixaListViewItem
        title={item ? "" : t("attachArticleString")}
        description={item ? <ItemCardDefault item={item} /> : ""}
        disabled={disabled}
        onClick={handleListViewItemClick}
        control="navigational"
        controlIcon={null}
        inset
        addOn={
          isError ? <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn> : null
        }
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-attach-article-header"
          header={
            <FixaModalHeader
              id="task-management-attach-article-header"
              title={t("attachArticleString")}
              closeBtnClick={handleCloseClick}
            />
          }
          footer={null}
          fullSize
        >
          {content}
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={showToast}
        text={toastMessage}
        onCloseRequest={() => setShowToast(false)}
      />
    </>
  );
};
